/**
 * Wires @rista/core's ONE remaining seam to the web app. Imported for SIDE EFFECTS ONLY, and it
 * must be imported BEFORE anything that can issue a request or run shared logic — see index.tsx.
 *
 * `effects-bridge` gives core the things it can never own: the toast (UI), the Sentry/analytics
 * sink, the logout teardown, and the host `window`. It is a STOPGAP whose entries should shrink
 * over time — see effects-bridge.ts.
 *
 * ⚠️ **There is no store wiring here any more.** `store-bridge` was deleted 2026-08-27: core owns
 * the store (`@rista/core/redux-store`) and this app's `redux-store` module is a re-export of it,
 * so handing that object back to core was wiring a singleton to itself. Core's state readers now
 * import the store directly.
 */
import { initEffectsBridge } from "@rista/core/effects-bridge";
import { registerKioskPaymentTransport } from "@rista/core/kiosk/payment/transport";
import { registerPrintHost, startPolling } from "@rista/core/printing";
import { webProviderTransport, webTransport } from "./paymentsEngine/transport";
import { webPrintHost } from "./printing";
import { ToastNotifyError, ToastNotifySuccess, ToastNotifyNotification } from "commonComponents/ToastNotify";
import { handleLogoutAction } from "helper/commonHelpers";
import { RISTA_API_URL, RECOMMENDATION_ENGINE_URL } from "./config";
import { isBetaOrProdEnv } from "data/env-constants";
import GitInfo from "helper/generatedGitInfo.json";
import { sendGTMEvent } from "helper/ga-helper";
import { printSaleTickets } from "helper/printHelper";
import { stopDesktopNotification } from "helper/myOrderHelpers";
import { pollForTickets } from "utils/polling";
// my-orders v2's save path (D14) — web-only, see the entry below
import { updateAdhocTables } from "helper/adhocTableHelpers";


initEffectsBridge({
  notifyError: ToastNotifyError,
  notifySuccess: ToastNotifySuccess,
  // Added 2026-08-25 for `middleware/notificationsListener`. NOT a styling variant of
  // notifySuccess: it dismisses the previous notification toast and plays a looping audio clip,
  // neither of which core can do. Only this app wires it.
  notifyNotification: ToastNotifyNotification,
  // webpack INLINES process.env.REACT_APP_* at build time, so shared code cannot read it —
  // the value has to be handed over from here, where the inlining happens.
  env: {
    dotpeApiUrl: process.env.REACT_APP_DOTPE_API_URL,
    ristaApiUrl: RISTA_API_URL,
    recommendationEngineUrl: RECOMMENDATION_ENGINE_URL,
    healthServiceUrl: process.env.REACT_APP_HEALTH_SERVICE_URL,
    // The item-image CDN origin, read by core's `getImageUrl`. Mobile derives the same value
    // from its react-native-config module, which is why the URL crosses the bridge rather than
    // being read in shared code.
    ristaItemBaseUrl: process.env.REACT_APP_RISTA_ITEM_BASE_URL,
    // The payment-provider logo host, read by core's `getPaymentIconsUrl`.
    paymentIconBaseUrl: process.env.REACT_APP_RISTA_EXTERNAL_PAYMENT_ICON,
    // Read by core's `isSubwayAccount`, the gate behind `shouldCalculateSale` and
    // `preventCloseSale`. Same reason as the URLs above: webpack inlines REACT_APP_* at build
    // time, so a shared module can never read it itself.
    subwayAccountUuid: process.env.REACT_APP_SUBWAY_ACCOUNT_UUID,
    // `shouldCalculateSale`'s other half, now shared (My Orders v2 recalculates on both
    // platforms): this build's own flag, computed from REACT_APP_ENVIRONMENT at module scope.
    isBetaOrProdEnv,
    // Build-time git values, read by core's `getCurrentVersions` for the login metadata. They
    // live in helper/generatedGitInfo.json, which `npm run git-info` REGENERATES per build and
    // which sits inside this app — core cannot import it, and it is plain data, so it comes
    // across the same way the URLs do.
    oracleGitInfo: GitInfo.oracleGitInfo,
    commonGitInfo: GitInfo.commonGitInfo,
    ristaPosCommonGitInfo: GitInfo.ristaPosCommonGitInfo,
    // The `?atoken=` query parameter, captured ONCE here rather than read per request:
    // `utils/apiService` used to capture it at its own module load, and it must stay the value
    // the page was opened with — the SPA rewrites the URL as the user navigates.
    urlAtoken: new URLSearchParams(window.location.search).get("atoken"),
  },
  // Web has no Sentry breadcrumb helper equivalent to mobile's `logSentryEvent` on this path;
  // mobile wires its own. Left as the bridge's no-op default rather than inventing one.
  //
  // The three below were added 2026-08-23 to unblock the last saga domains. Each is a capability
  // core genuinely cannot own: `sendGTMEvent` is 203 lines on react-ga4, `printSaleTickets` sits
  // on ~1100 lines of printer glue, and `pollForTickets` on a 900-line poller that imports it.
  sendGTMEvent,
  printSaleTickets,
  // For the shared my-orders layer (`@rista/core/myOrders`): the new-order alert, an audio
  // capability core cannot own. Its prints go through `@rista/core/printing` (2026-10-08).
  stopNewOrderAlert: stopDesktopNotification,
  // 2026-09-17, my-orders v2's own save path (D14). Mobile wires the same since the
  // release_4.0.0 merge of 2026-10-05, which brought adhoc tables to it.
  refreshAdhocTables: updateAdhocTables,
  // The device's ONE print poller: the package's where this branch prints through it, v1's
  // everywhere else. Both the POS-settings saga (every settings fetch) and the printing flow's
  // hand-off come through here, so the two loops can never both run.
  pollForTickets: () => {
    if (!startPolling()) pollForTickets();
  },
  logout: handleLogoutAction,
  window,
});

/**
 * The payment ENGINE's transport, for the kiosk.
 *
 * SEPARATE from `initEffectsBridge` on purpose: every entry on that seam degrades
 * to a no-op when unwired, because "dropping a toast is strictly better than
 * turning a successful API response into an exception". A transport is the
 * opposite — it is the only thing that can write the order — so it has its own
 * registry, which reports its absence instead of swallowing it. See
 * `@rista/core/kiosk/payment/transport`.
 *
 * The SAME object `PaymentEngineCheckout` hands the POS checkout, engine calls
 * and provider-specific calls merged, so there is one definition of how this app
 * talks to the payment backend and the kiosk cannot drift from the terminal.
 */
registerKioskPaymentTransport({ ...webTransport, ...webProviderTransport });

/**
 * The printer host for `@rista/printing` — the revamped printing flow. Same reasoning as the
 * payment transport above: a registry that reports its absence (`getPrinting()` is null) rather
 * than an effects-bridge entry that would swallow it.
 *
 * The kiosk and My Orders v2 (`@rista/core/myOrders`) print through it on every branch — they
 * call `@rista/core/printing` directly, no gate. The rest of the POS (Cart, checkout and tableView
 * sagas, `useCancellationFlow`, `saleHelpers`) keeps `helper/printHelper` via the bridge's
 * `printSaleTickets` until the rollout gate lists the branch (docs/flows/PRINTING.md §7); only then
 * can that entry leave the effects bridge. `pollForTickets` already asks the gate.
 */
registerPrintHost(webPrintHost);
